import { editsOf, findSection, value } from '../../parser/fortigate'
import { finding, type Finding, type Rule } from '../types'

/**
 * Device-hardening checks: the settings that are wrong by omission rather than by
 * commission. Nobody deliberately turns the pre-login banner off — it was simply never
 * turned on, and it is invisible until an auditor asks.
 */

/** Certificates FortiOS ships with, which are self-signed and identical on every unit. */
const FACTORY_CERTS = ['fortinet_factory', 'fortinet_factory2', 'self-sign']

export const noDnsConfigured: Rule = {
  id: 'fg-no-dns',
  title: 'No DNS servers configured',
  run(config) {
    const dns = findSection(config, 'system', 'dns')
    if (dns === undefined) return []
    if (value(dns, 'primary') !== undefined || value(dns, 'server-hostname') !== undefined) return []

    return [
      finding(noDnsConfigured, {
        severity: 'low',
        title: 'No DNS server is configured',
        detail:
          'system dns has no primary server set. Features that resolve names — FortiGuard updates, ' +
          'FQDN address objects, web filtering — fail silently or fall back unpredictably.',
        location: 'system dns',
        line: dns.line,
        remediation: 'config system dns\n    set primary 1.1.1.1\n    set secondary 9.9.9.9\nend',
      }),
    ]
  },
}

export const noPreLoginBanner: Rule = {
  id: 'fg-no-pre-login-banner',
  title: 'Pre-login banner not enabled',
  run(config) {
    const global = findSection(config, 'system', 'global')
    if (global === undefined) return []
    if (value(global, 'pre-login-banner') === 'enable') return []

    return [
      finding(noPreLoginBanner, {
        severity: 'low',
        title: 'No banner is shown before login',
        detail:
          'pre-login-banner is not enabled. Many jurisdictions and most incident-response policies ' +
          'expect an explicit notice before authentication, and its absence has been used to argue ' +
          'that access was not clearly unauthorised.',
        location: 'system global',
        line: global.line,
        remediation: 'config system global\n    set pre-login-banner enable\nend',
      }),
    ]
  },
}

export const noPostLoginBanner: Rule = {
  id: 'fg-no-post-login-banner',
  title: 'Post-login banner not enabled',
  run(config) {
    const global = findSection(config, 'system', 'global')
    if (global === undefined) return []
    if (value(global, 'post-login-banner') === 'enable') return []

    return [
      finding(noPostLoginBanner, {
        severity: 'info',
        title: 'No banner is shown after login',
        detail:
          'post-login-banner is not enabled, so an administrator sees no acceptable-use notice ' +
          'once authenticated.',
        location: 'system global',
        line: global.line,
        remediation: 'config system global\n    set post-login-banner enable\nend',
      }),
    ]
  },
}

export const defaultHostname: Rule = {
  id: 'fg-default-hostname',
  title: 'Hostname left at a factory value',
  run(config) {
    const global = findSection(config, 'system', 'global')
    const hostname = value(global, 'hostname')
    if (hostname === undefined) return []
    // Factory hostnames are the model plus serial, e.g. FGT60E4Q16000000 or FortiGate-60E.
    if (!/^(fgt|fortigate|fgvm|fwf)[-_]?[a-z0-9]*$/i.test(hostname)) return []

    return [
      finding(defaultHostname, {
        severity: 'info',
        title: `Hostname "${hostname}" looks like a factory default`,
        detail:
          'The hostname still matches the model-and-serial pattern FortiOS ships with. Log lines and ' +
          'alerts from several devices become hard to tell apart, which slows every investigation.',
        location: 'system global',
        line: global?.line,
        remediation: 'config system global\n    set hostname "SITE-ROLE-01"\nend',
      }),
    ]
  },
}

export const noTimezone: Rule = {
  id: 'fg-no-timezone',
  title: 'Timezone not configured',
  run(config) {
    const global = findSection(config, 'system', 'global')
    if (global === undefined) return []
    if (value(global, 'timezone') !== undefined) return []

    return [
      finding(noTimezone, {
        severity: 'low',
        title: 'No timezone is set',
        detail:
          'system global has no timezone. Even with NTP correct, log timestamps are rendered in the ' +
          'default zone, which makes comparing this device against local incident records error-prone.',
        location: 'system global',
        line: global.line,
        pci: '10.6.1',
        remediation: 'config system global\n    set timezone 04\nend  # 04 = (GMT+9:00) Seoul',
      }),
    ]
  },
}

export const usbAutoInstall: Rule = {
  id: 'fg-usb-autoinstall',
  title: 'USB auto-install enabled',
  run(config) {
    const autoInstall = findSection(config, 'system', 'auto-install')
    if (autoInstall === undefined) return []

    const enabled = ['auto-install-config', 'auto-install-image'].filter(
      (key) => value(autoInstall, key) === 'enable',
    )
    if (enabled.length === 0) return []

    return [
      finding(usbAutoInstall, {
        severity: 'high',
        title: 'The device will load configuration or firmware from a USB stick',
        detail:
          `system auto-install has ${enabled.join(' and ')} enabled. Anyone with brief physical access ` +
          'can replace the configuration or the firmware by plugging in a USB device and power-cycling.',
        location: 'system auto-install',
        line: autoInstall.line,
        remediation:
          'config system auto-install\n    set auto-install-config disable\n' +
          '    set auto-install-image disable\nend',
      }),
    ]
  },
}

export const defaultAdminPorts: Rule = {
  id: 'fg-default-admin-ports',
  title: 'Administrative services on default ports',
  run(config) {
    const global = findSection(config, 'system', 'global')
    if (global === undefined) return []

    const onDefault: string[] = []
    if ((value(global, 'admin-sport') ?? '443') === '443') onDefault.push('HTTPS on 443')
    if ((value(global, 'admin-ssh-port') ?? '22') === '22') onDefault.push('SSH on 22')
    if (onDefault.length === 0) return []

    return [
      finding(defaultAdminPorts, {
        severity: 'info',
        title: `Management services are on their default ports (${onDefault.join(', ')})`,
        detail:
          'Default management ports are the first thing mass scanners probe. Moving them is not a ' +
          'security control on its own, but it removes this device from the untargeted noise that ' +
          'fills the logs and drives most credential-stuffing attempts.',
        location: 'system global',
        line: global.line,
        remediation: 'config system global\n    set admin-sport 8443\n    set admin-ssh-port 2222\nend',
      }),
    ]
  },
}

export const weakAdminLockout: Rule = {
  id: 'fg-weak-admin-lockout',
  title: 'Administrator lockout not hardened',
  run(config) {
    const global = findSection(config, 'system', 'global')
    if (global === undefined) return []

    const threshold = Number(value(global, 'admin-lockout-threshold') ?? '3')
    const duration = Number(value(global, 'admin-lockout-duration') ?? '60')
    const problems: string[] = []
    if (!Number.isFinite(threshold) || threshold > 5) problems.push(`${threshold} failed attempts allowed`)
    if (!Number.isFinite(duration) || duration < 60) problems.push(`lockout lasts only ${duration}s`)
    if (problems.length === 0) return []

    return [
      finding(weakAdminLockout, {
        severity: 'medium',
        title: 'Administrator lockout is weak',
        detail:
          `Login lockout is configured such that ${problems.join(' and ')}. That leaves online ` +
          'password guessing against the management interface practical rather than merely slow.',
        location: 'system global',
        line: global.line,
        // No PCI citation: the lockout requirement's sub-number has not been verified.
        remediation:
          'config system global\n    set admin-lockout-threshold 3\n    set admin-lockout-duration 300\nend',
      }),
    ]
  },
}

export const sshV1Enabled: Rule = {
  id: 'fg-ssh-v1',
  title: 'SSH version 1 enabled',
  run(config) {
    const global = findSection(config, 'system', 'global')
    if (value(global, 'admin-ssh-v1') !== 'enable') return []

    return [
      finding(sshV1Enabled, {
        severity: 'high',
        title: 'SSH version 1 is enabled',
        detail:
          'admin-ssh-v1 is enabled. SSHv1 has unfixable protocol flaws that permit session ' +
          'manipulation, and it has been deprecated for over two decades.',
        location: 'system global',
        line: findSection(config, 'system', 'global')?.line,
        pci: '2.2.7',
        remediation: 'config system global\n    set admin-ssh-v1 disable\nend',
      }),
    ]
  },
}

export const factoryCertificate: Rule = {
  id: 'fg-factory-certificate',
  title: 'Factory certificate used for the management GUI',
  run(config) {
    const global = findSection(config, 'system', 'global')
    const cert = value(global, 'admin-server-cert')
    if (cert === undefined || !FACTORY_CERTS.includes(cert)) return []

    return [
      finding(factoryCertificate, {
        severity: 'medium',
        title: 'The management GUI presents the built-in factory certificate',
        detail:
          `admin-server-cert is set to ${cert}, which ships on every unit and is not trusted by any ` +
          'browser. Administrators are trained to click through the warning, so a machine-in-the-middle ' +
          'on the management path produces no signal they would notice.',
        location: 'system global',
        line: global?.line,
        pci: '2.2.7',
        remediation:
          'config system global\n    set admin-server-cert "your-issued-cert"\nend\n' +
          '# Import a certificate that chains to a CA your administrators already trust.',
      }),
    ]
  },
}

export const maintainerAccountEnabled: Rule = {
  id: 'fg-maintainer-enabled',
  title: 'Console maintainer account enabled',
  run(config) {
    const global = findSection(config, 'system', 'global')
    if (value(global, 'admin-maintainer') !== 'enable') return []

    return [
      finding(maintainerAccountEnabled, {
        severity: 'medium',
        title: 'The console password-recovery account is enabled',
        detail:
          'admin-maintainer is enabled, so anyone with physical console access and the device serial ' +
          'number can reset the administrator password during a reboot. That is a documented recovery ' +
          'path, not a vulnerability — but it means physical access equals full control, which matters ' +
          'wherever the device is not in a locked room.',
        location: 'system global',
        line: global?.line,
        // No PCI citation: the physical-access requirement's sub-number is unverified.
        remediation:
          'config system global\n    set admin-maintainer disable\nend\n' +
          '# Only disable this if you have another recovery plan. Locking yourself out is permanent.',
      }),
    ]
  },
}

export const staticKeyCiphers: Rule = {
  id: 'fg-static-key-ciphers',
  title: 'Static-key TLS ciphers enabled',
  run(config) {
    const global = findSection(config, 'system', 'global')
    if (value(global, 'ssl-static-key-ciphers') !== 'enable') return []

    return [
      finding(staticKeyCiphers, {
        severity: 'medium',
        title: 'Static-key TLS ciphers are permitted',
        detail:
          'ssl-static-key-ciphers is enabled, allowing cipher suites without forward secrecy. Traffic ' +
          'captured today stays decryptable by anyone who later obtains the server key.',
        location: 'system global',
        line: global?.line,
        pci: '2.2.7',
        remediation: 'config system global\n    set ssl-static-key-ciphers disable\nend',
      }),
    ]
  },
}

export const noHighAvailability: Rule = {
  id: 'fg-no-ha',
  title: 'High availability not configured',
  run(config) {
    const ha = findSection(config, 'system', 'ha')
    if (ha === undefined) return []
    const mode = value(ha, 'mode')
    if (mode !== undefined && mode !== 'standalone') return []

    return [
      finding(noHighAvailability, {
        severity: 'info',
        title: 'The device is standalone with no HA peer',
        detail:
          'system ha is in standalone mode. This is a deliberate choice for many sites, but it means ' +
          'the firewall is a single point of failure for everything behind it.',
        location: 'system ha',
        line: ha.line,
        remediation: '# Informational. Configure an HA cluster if the outage window justifies it.',
      }),
    ]
  },
}

export const noEventLogging: Rule = {
  id: 'fg-no-event-logging',
  title: 'Event logging disabled',
  run(config) {
    const eventFilter = findSection(config, 'log', 'eventfilter')
    if (eventFilter === undefined) return []
    if (value(eventFilter, 'event') !== 'disable') return []

    return [
      finding(noEventLogging, {
        severity: 'high',
        title: 'System event logging is switched off',
        detail:
          'log eventfilter has event set to disable, so administrative logins, configuration changes ' +
          'and system events are not recorded at all — including the change that turned it off.',
        location: 'log eventfilter',
        line: eventFilter.line,
        pci: '10.2.1',
        remediation: 'config log eventfilter\n    set event enable\nend',
      }),
    ]
  },
}

export const unusedAddressObjects: Rule = {
  id: 'fg-unused-address-objects',
  title: 'Address objects referenced by no policy',
  run(config) {
    const addresses = editsOf(config, 'firewall', 'address')
    if (addresses.length === 0) return []

    const referenced = new Set<string>()
    for (const policy of editsOf(config, 'firewall', 'policy')) {
      for (const key of ['srcaddr', 'dstaddr']) {
        for (const name of policy.sets[key] ?? []) referenced.add(name.toLowerCase())
      }
    }
    for (const group of editsOf(config, 'firewall', 'addrgrp')) {
      for (const name of group.sets['member'] ?? []) referenced.add(name.toLowerCase())
    }

    const orphans = addresses
      .map((a) => a.key)
      .filter((name) => name !== '' && !referenced.has(name.toLowerCase()))

    // Below the threshold this is noise; above it, it is a real review burden.
    if (orphans.length < 5) return []

    const shown = orphans.slice(0, 8).join(', ')
    return [
      finding(unusedAddressObjects, {
        severity: 'info',
        title: `${orphans.length} address objects are not used by any policy`,
        detail:
          `Objects not referenced by any policy or address group: ${shown}` +
          (orphans.length > 8 ? `, and ${orphans.length - 8} more.` : '.') +
          ' Each one is something a reviewer has to read and dismiss, and a stale object with a ' +
          'familiar name is how the wrong subnet ends up in a new rule.',
        location: 'firewall address',
        pci: '1.2.7',
        remediation:
          '# Confirm each is genuinely unused, then:\nconfig firewall address\n    delete "<name>"\nend',
      }),
    ]
  },
}

export const HARDENING_RULES: Rule[] = [
  noDnsConfigured,
  noPreLoginBanner,
  noPostLoginBanner,
  defaultHostname,
  noTimezone,
  usbAutoInstall,
  defaultAdminPorts,
  weakAdminLockout,
  sshV1Enabled,
  factoryCertificate,
  maintainerAccountEnabled,
  staticKeyCiphers,
  noHighAvailability,
  noEventLogging,
  unusedAddressObjects,
]

export type { Finding }
